/**
 * @file src/pages/invoice-management-hub/components/TemplateSelectionModal.jsx
 * @description Modal that lets the user pick a PDF template, then either
 * previews the PDF in an in-app viewer or triggers a browser download.
 *
 * Replaces the previous popup-window-with-document.write approach. Reasons:
 *  - popup blockers killed it for most users
 *  - the old code interpolated invoice fields into raw HTML/JS strings,
 *    a latent XSS surface
 *  - cross-window globals (window.dismissToast) were fragile
 *
 * New flow:
 *  - "view"     → blob URL rendered inside an in-app iframe modal
 *  - "download" → blob piped to triggerBrowserDownload (same helper used
 *                 for XML download, defined in api/invoice.api.js)
 *
 * Blob URLs are revoked when the preview modal closes, so we don't leak
 * memory across multiple previews.
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import {
  generateInvoicePdfBlob,
  triggerBrowserDownload,
} from "api/invoice.api";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";

const TEMPLATES = [
  { id: 1, name: "Template 1", image: "/assets/templates/temp1.png" },
  { id: 2, name: "Template 2", image: "/assets/templates/temp2.png" },
  { id: 3, name: "Template 3", image: "/assets/templates/temp3.png" },
];

// ---------------------------------------------------------------------------
// PdfPreviewModal — in-app PDF viewer (replaces the popup window)
// ---------------------------------------------------------------------------

const PdfPreviewModal = ({ pdfUrl, invoiceNumber, onClose }) => {
  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-[10000] flex items-center justify-center p-4"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-label={`Invoice ${invoiceNumber} PDF preview`}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Invoice {invoiceNumber}
            </h2>
            <p className="text-sm text-muted-foreground">PDF preview</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline px-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
            >
              Open in new tab
            </a>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label="Close preview"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* PDF */}
        <iframe
          src={pdfUrl}
          title={`Invoice ${invoiceNumber} PDF`}
          className="flex-1 w-full bg-white"
        />
      </motion.div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// TemplateSelectionModal
// ---------------------------------------------------------------------------

const TemplateSelectionModal = ({ invoice, action, onClose }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Preview state — only used when action === "view"
  const [previewUrl, setPreviewUrl] = useState(null);

  // Always revoke blob URLs when the preview closes
  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    onClose();
  };

  // Safety net: revoke on unmount in case the component is torn down
  // without closePreview being called
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleProceed = async () => {
    if (!selectedTemplate || !invoice?.id) {
      toast.error("Please select a template");
      return;
    }

    setIsProcessing(true);
    const loadingToast = toast.loading(
      `${action === "view" ? "Generating" : "Preparing"} PDF for invoice ${invoice.invoiceNumber}...`,
    );

    try {
      const pdfBlob = await generateInvoicePdfBlob(
        invoice.id,
        selectedTemplate,
      );

      if (action === "view") {
        const url = URL.createObjectURL(pdfBlob);
        setPreviewUrl(url);
        toast.dismiss(loadingToast);
        toast.success(`PDF opened for invoice ${invoice.invoiceNumber}`);
        // Note: we do NOT call onClose() here — the PdfPreviewModal renders
        // on top of this one and uses closePreview to dismiss both.
      } else {
        triggerBrowserDownload(
          pdfBlob,
          `Invoice_${invoice.invoiceNumber}_Template${selectedTemplate}.pdf`,
        );
        toast.dismiss(loadingToast);
        toast.success(
          `PDF download started for invoice ${invoice.invoiceNumber}`,
        );
        onClose();
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(
        error?.message || `Failed to ${action} PDF. Please try again.`,
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border max-w-4xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Select Invoice Template
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Choose a template to {action === "view" ? "view" : "download"}{" "}
                invoice {invoice.invoiceNumber}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>

          {/* Templates */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {TEMPLATES.map((template) => (
                <motion.div
                  key={template.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                    selectedTemplate === template.id
                      ? "border-primary shadow-lg ring-2 ring-primary/20"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <div className="aspect-[4/3] bg-muted relative overflow-hidden">
                    <img
                      src={template.image}
                      alt={template.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    {selectedTemplate === template.id && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-3 right-3 w-8 h-8 bg-primary rounded-full flex items-center justify-center shadow-lg"
                      >
                        <Icon
                          name="CheckCircle2"
                          size={20}
                          className="text-primary-foreground"
                        />
                      </motion.div>
                    )}
                  </div>
                  <div className="p-4 bg-card">
                    <h3 className="font-medium text-gray-900 dark:text-white text-center">
                      {template.name}
                    </h3>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {selectedTemplate
                ? `Template ${selectedTemplate} selected`
                : "Please select a template to continue"}
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={onClose}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="md"
                iconName={action === "view" ? "Eye" : "Download"}
                onClick={handleProceed}
                disabled={!selectedTemplate || isProcessing}
                className="min-w-[120px]"
              >
                {isProcessing
                  ? "Processing..."
                  : action === "view"
                    ? "View PDF"
                    : "Download PDF"}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* In-app preview modal — replaces the popup window entirely */}
      <AnimatePresence>
        {previewUrl && (
          <PdfPreviewModal
            pdfUrl={previewUrl}
            invoiceNumber={invoice.invoiceNumber}
            onClose={closePreview}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default TemplateSelectionModal;
